import { Badge } from "@/core/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/core/components/ui/card";
import { NoTranslate } from "@/core/components/ui/no-translate";
import useTypedTranslation, {
  type TFunctionType,
} from "@/core/hooks/use-typed-translation";
import { useSessionStore } from "@/core/stores/session.store";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";

const claimString = (
  claims: Record<string, unknown>,
  key: string
): string | undefined => {
  const value = claims[key];
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    const joined = value.filter((item) => typeof item === "string").join(", ");
    return joined || undefined;
  }
  return undefined;
};

const unixToIso = (value: unknown): string | undefined => {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  return new Date(value * 1000).toISOString();
};

/**
 * Profil opérateur — identité lue dans le store de session (claims du
 * access token Keycloak). Aucun appel API dédié.
 */
export function ProfilePage() {
  const { t } = useTypedTranslation();
  const userInfo = useSessionStore((s) => s.userInfo);
  const claims = (userInfo ?? {}) as Record<string, unknown>;

  if (!userInfo) {
    return (
      <section className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("profile.title")}
        </h1>
        <p className="text-muted-foreground">{t("profile.empty")}</p>
      </section>
    );
  }

  const missing = t("profile.missingClaim");
  const display = (value: string | undefined) => value?.trim() || missing;
  const sub = claimString(claims, "sub");
  const username = claimString(claims, "preferred_username");
  const email = claimString(claims, "email");
  const name = claimString(claims, "name");
  const givenName = claimString(claims, "given_name");
  const familyName = claimString(claims, "family_name");
  const aud = claimString(claims, "aud");
  const iss = claimString(claims, "iss");
  const expIso = unixToIso(claims.exp);
  const iatIso = unixToIso(claims.iat);
  const roles = Array.isArray(
    (claims.realm_access as { roles?: unknown } | undefined)?.roles
  )
    ? ((claims.realm_access as { roles: unknown[] }).roles.filter(
        (role) => typeof role === "string"
      ) as string[])
    : [];
  const nowSec = Date.now() / 1000;
  const expired =
    typeof claims.exp === "number" ? claims.exp < nowSec : undefined;

  const rows: { label: TFunctionType; value: string; identity?: boolean }[] = [
      { label: "profile.fields.sub", value: display(sub), identity: true },
      { label: "profile.fields.username", value: display(username), identity: true },
      { label: "profile.fields.email", value: display(email), identity: true },
      { label: "profile.fields.name", value: display(name) },
      { label: "profile.fields.givenName", value: display(givenName) },
      { label: "profile.fields.familyName", value: display(familyName) },
      { label: "profile.fields.aud", value: display(aud), identity: true },
      { label: "profile.fields.iss", value: display(iss), identity: true },
      {
        label: "profile.fields.exp",
        value: formatDateWithFallback(expIso, missing),
      },
      {
        label: "profile.fields.iat",
        value: formatDateWithFallback(iatIso, missing),
      },
    ];

  return (
    <section className="flex max-w-3xl flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold text-foreground">
          {t("profile.title")}
        </h1>
        <p className="text-muted-foreground">{t("profile.subtitle")}</p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-3">
          <div>
            <CardTitle>{t("profile.title")}</CardTitle>
            <CardDescription>{t("profile.subtitle")}</CardDescription>
          </div>
          {expired !== undefined ? (
            <Badge variant={expired ? "destructive" : "success"}>
              {t(expired ? "profile.tokenExpired" : "profile.tokenValid")}
            </Badge>
          ) : null}
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {rows.map((row) => (
            <div key={row.label} className="flex flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t(row.label)}
              </span>
              {row.identity && row.value !== missing ? (
                <NoTranslate as="span" className="text-sm">
                  {row.value}
                </NoTranslate>
              ) : (
                <span className="text-sm">{row.value}</span>
              )}
            </div>
          ))}

          <div className="flex flex-col gap-2 sm:col-span-2">
            <span className="text-xs font-medium text-muted-foreground">
              {t("profile.fields.roles")}
            </span>
            {roles.length ? (
              <div className="flex flex-wrap gap-2">
                {roles.map((role) => (
                  <Badge key={role} variant="secondary">
                    <NoTranslate as="span">{role}</NoTranslate>
                  </Badge>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                {t("profile.roles.empty")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
